import { useEffect, useState } from 'react';

import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone.jsx';
import { accessErrorMessage, saveAccessRule } from '../../../services/AccessRequestService';

/**
 * What one role may ask for -- a view popup in the attendance popup's frame,
 * with Save beside Close. `rule` is a row of GET /accessrequests/rules and
 * `roles` every row of it, so each other role can be ticked.
 */
const AccessRuleModal = ({ rule, roles, onClose, onSaved }) => {
  const [picked, setPicked] = useState(() => new Set((rule?.requestable || []).map((r) => r.id)));
  const [saving, setSaving] = useState(false);
  // A save swaps the form for the done panel, which closes the popup five
  // seconds later.
  const { done } = usePopupDone(Boolean(rule));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!rule) return null;

  const others = (roles || []).filter((r) => r.roleId !== rule.roleId);

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveAccessRule(rule.roleId, [...picked]);
      toast.success(`Saved what ${rule.roleLabel} can request`);
      onSaved();
      setSaving(false);
    } catch (error) {
      toast.error(accessErrorMessage(error, 'Could not save the rule'));
      setSaving(false);
    }
  };

  return (
    <div className="pg-modal" role="dialog" aria-modal="true" aria-labelledby="pgm-rule-title" onClick={onClose}>
      <div className="pg-modal-panel" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-rule-title">
              {rule.roleLabel}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Can request</dt>
                <dd>{picked.size ? `${picked.size} ${picked.size === 1 ? 'role' : 'roles'}` : 'Nothing'}</dd>
              </div>
            </dl>
          </div>

          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Roles a {rule.roleLabel} may ask for</h3>
                {others.length === 0 ? (
                  <p className="pgm-empty">There are no other roles.</p>
                ) : (
                  <div className="acc-choices">
                    {others.map((r) => (
                      <label className="acc-choice" key={r.roleId}>
                        <input type="checkbox" checked={picked.has(r.roleId)} onChange={() => toggle(r.roleId)} />
                        <span>{r.roleLabel}</span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="pgm-empty">
                  Anyone holding {rule.roleLabel} can then send a request for these. Nothing is granted until
                  an administrator approves it.
                </p>
              </section>
            </>
          )}
        </div>

        {/* The done panel replaces Save once it lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              <button type="button" className="pgm-btn" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default AccessRuleModal;
